<template>
  <full-screen
    :title="t('end-shift.title')"
    :show-close="true"
    :show-back="false"
    @close="closePage"
  >
    <div class="min-h-full w-full flex flex-col gap-6 max-w-[52rem] py-4 sm:py-8 px-4 sm:mx-auto">
      <div v-if="todayReport" class="flex flex-col gap-4">
        <div class="flex items-center gap-3">
          <l-icon
            size="large"
            name="lucide/trending-up"
            class="text-v-300"
            :original-color="true"
          />
          <h2 class="heading-3 text-n-0">{{ t('end-shift.todays-performance') }}</h2>
        </div>
        <div
          class="flex sm:grid sm:grid-cols-3 gap-4 overflow-x-auto scrolling-touch -mx-4 px-4 sm:mx-0 sm:px-0"
        >
          <div class="border border-n-900 rounded-2xl p-4 pl-6 min-w-[90%] sm:min-w-0 shrink-0">
            <div class="flex items-start gap-3 mb-3">
              <div class="flex-1">
                <div class="body-4 text-n-400 mb-1">{{ t('end-shift.total-revenue') }}</div>
                <div class="text-1 text-n-0 text-3xl">
                  {{ currency(todayReport.breakdown.total) }}
                </div>
              </div>
              <div class="w-10 h-10 rounded-xl flex items-center justify-center bg-n-900">
                <l-icon
                  name="lucide/receipt-text"
                  size="medium"
                  class="text-n-400"
                  :original-color="true"
                />
              </div>
            </div>
          </div>

          <div class="border border-n-900 rounded-2xl p-4 pl-6 min-w-[90%] sm:min-w-0 shrink-0">
            <div class="flex items-start gap-3 mb-3">
              <div class="flex-1">
                <div class="body-4 text-n-400 mb-1">{{ t('end-shift.orders') }}</div>
                <div class="text-1 text-n-0 text-3xl">{{ todayReport.stats.totalTabs }}</div>
              </div>
              <div class="w-10 h-10 rounded-xl flex items-center justify-center bg-n-900">
                <l-icon
                  name="lucide/shopping-bag"
                  size="medium"
                  class="text-n-400"
                  :original-color="true"
                />
              </div>
            </div>
          </div>

          <div class="border border-n-900 rounded-2xl p-4 pl-6 min-w-[90%] sm:min-w-0 shrink-0">
            <div class="flex items-start gap-3 mb-3">
              <div class="flex-1">
                <div class="body-4 text-n-400 mb-1">{{ t('end-shift.avg-ticket') }}</div>
                <div class="text-1 text-n-0 text-3xl">
                  {{ currency(todayReport.stats.averagePerTab) }}
                </div>
              </div>
              <div class="w-10 h-10 rounded-xl flex items-center justify-center bg-n-900">
                <l-icon
                  name="lucide/receipt-euro"
                  size="medium"
                  class="text-n-400"
                  :original-color="true"
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="flex flex-col gap-4">
        <div class="flex items-center gap-3">
          <l-icon
            size="large"
            name="lucide/list-checks"
            class="text-v-300"
            :original-color="true"
          />
          <h2 class="heading-3 text-n-0">{{ t('end-shift.closing-checklist') }}</h2>
        </div>
        <div class="bg-n-900 rounded-2xl p-4 flex items-center gap-6">
          <div
            class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            :class="allTabsClosed ? 'bg-g-400/30' : 'bg-y-500/30'"
          >
            <l-icon
              name="ticket"
              size="medium"
              :class="allTabsClosed ? 'text-g-400' : 'text-y-500'"
            />
          </div>
          <div class="flex-1 min-w-0">
            <div class="body-1 mb-1 truncate">
              {{ t('end-shift.tabs.title') }}
            </div>
            <div class="body-3 text-n-400 truncate">
              {{ tabsStatus }}
            </div>
          </div>
          <l-button v-if="!allTabsClosed" variant="secondary" @click="navigateToTabs">
            {{ t('end-shift.tabs.action') }}
          </l-button>
          <div v-else class="flex items-center gap-2">
            <l-icon name="check-circle" class="text-g-400 text-xl" />
            <span class="body-3 text-g-400">{{ t('end-shift.no-open-tabs') }}</span>
          </div>
        </div>

        <div class="bg-n-900 rounded-2xl p-4 flex items-center gap-6">
          <div
            class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            :class="allTillsClosed ? 'bg-g-400/30' : 'bg-y-500/30'"
          >
            <l-icon
              name="cash"
              size="medium"
              :class="allTillsClosed ? 'text-g-400' : 'text-y-500'"
            />
          </div>
          <div class="flex-1 min-w-0">
            <div class="body-1 mb-1 truncate">
              {{ t('end-shift.tills-card.title') }}
            </div>
            <div class="body-3 text-n-400 truncate">
              {{ tillsStatus }}
            </div>
          </div>
          <l-button v-if="!allTillsClosed" variant="secondary" @click="handleShowTillsModal">
            {{ t('end-shift.tills-card.action') }}
          </l-button>
          <div v-else class="flex items-center gap-2">
            <l-icon name="check-circle" class="text-g-400 text-xl" />
            <span class="body-3 text-g-400">{{ t('end-shift.no-open-tills') }}</span>
          </div>
        </div>

        <div class="bg-n-900 rounded-2xl p-4 flex items-center gap-6">
          <div
            class="w-10 h-10 rounded-xl flex items-center justify-center shrink-0"
            :class="allEmployeesClockedOut ? 'bg-g-400/30' : 'bg-y-500/30'"
          >
            <l-icon
              name="people"
              size="medium"
              :class="allEmployeesClockedOut ? 'text-g-400' : 'text-y-500'"
            />
          </div>
          <div class="flex-1 min-w-0">
            <div class="body-1 mb-1 truncate">
              {{ t('end-shift.employees-card.title') }}
            </div>
            <div class="body-3 text-n-400 truncate">
              {{ employeesStatus }}
            </div>
          </div>
          <l-button
            v-if="!allEmployeesClockedOut"
            variant="secondary"
            @click="showEmployeesModal = true"
          >
            {{ t('end-shift.employees-card.action') }}
          </l-button>
          <div v-else class="flex items-center gap-2">
            <l-icon name="check-circle" class="text-g-400 text-xl" />
            <span class="body-3 text-g-400">{{ t('end-shift.all-employees-clocked-out') }}</span>
          </div>
        </div>
      </div>

      <div class="w-full mt-auto flex gap-4">
        <template v-if="dayShift.end">
          <l-button class="w-full" @click="() => printZ()">
            {{ t('end-shift.print-z-report') }}
          </l-button>
        </template>
        <template v-else>
          <l-button
            class="w-full"
            type="secondary"
            :disabled="startedShifts.length > 0"
            @click="closeShift"
          >
            {{ t('end-shift.end-shift') }}
          </l-button>
          <l-button class="w-full" :disabled="startedShifts.length > 0" @click="tryCloseDay">
            {{ t('end-shift.close-day-generate') }}
          </l-button>
        </template>
      </div>

      <l-modal
        v-if="showTillsModal"
        @close="showTillsModal = false"
        :title="t('end-shift.tills')"
        :description="t('end-shift.tills-pending-description', { count: startedShifts.length })"
      >
        <div class="flex flex-col gap-4 mb-4">
          <div
            v-for="till in openTills"
            :key="till.name"
            class="bg-n-700 rounded-lg p-4 flex items-center gap-6"
          >
            <div class="flex-1">
              <div class="body-1 mb-1">{{ till.name }}</div>
              <div class="body-3 text-n-400">
                {{ t('end-shift.till-open') }}
              </div>
            </div>
            <l-button variant="secondary" @click="startClosingCashTill(till.id)">
              {{ t('end-shift.close-till-action') }}
            </l-button>
          </div>
          <div v-if="!openTills || openTills.length === 0" class="text-n-300 text-center py-4">
            {{ t('end-shift.no-tills') }}
          </div>
        </div>
      </l-modal>

      <l-modal
        v-if="showEmployeesModal"
        @close="showEmployeesModal = false"
        :title="t('end-shift.employees')"
      >
        <div class="flex flex-col gap-4">
          <div
            v-for="employee in workingEmployees"
            :key="employee.name"
            class="border border-n-600 bg-n-700 rounded-xl flex items-center gap-4 h-16 p-4"
          >
            <div
              class="uppercase text-xl font-bold w-10 h-10 flex items-center justify-center rounded-full shrink-0"
              :class="[getColorClass(employee.position)]"
            >
              {{ shortName(employee.name) }}
            </div>
            <div class="flex-1">
              <div class="font-heading font-medium text-n-0 mb-0.5">{{ employee.name }}</div>
              <div class="text-xs text-n-400">
                {{ t('employees.attendance.clocked-in') }} -
                {{ formatClockInTime(employee.clockIn!) }}
              </div>
            </div>
          </div>
          <div
            v-if="!workingEmployees || workingEmployees.length === 0"
            class="text-n-300 text-center py-4"
          >
            {{ t('end-shift.all-employees-clocked-out') }}
          </div>
        </div>
      </l-modal>

      <l-modal
        v-if="showZReportModal && zReport"
        :title="t('report.z-report')"
        :backdrop-closable="true"
        @close="showZReportModal = false"
      >
        <z-report-summary :report="zReport" />
        <template #footer>
          <div class="flex gap-3 w-full">
            <l-button class="flex-1" type="secondary" @click="() => printZ(zReport)">
              {{ t('end-shift.print-z-report') }}
            </l-button>
            <l-button class="flex-1" @click="onDayClosed">
              {{ t('ctas.continue') }}
            </l-button>
          </div>
        </template>
      </l-modal>

      <l-modal
        v-if="showXReportModal && xReport"
        :title="t('report.x-report')"
        :backdrop-closable="true"
        @close="showXReportModal = false"
      >
        <z-report-summary :report="xReport" />
        <template #footer>
          <div class="flex gap-3 w-full">
            <l-button class="flex-1" type="secondary" @click="() => printX(xReport)">
              {{ t('end-shift.print-x-report') }}
            </l-button>
            <l-button class="flex-1" @click="onShiftClosed">
              {{ t('ctas.continue') }}
            </l-button>
          </div>
        </template>
      </l-modal>
    </div>
  </full-screen>
</template>

<script setup lang="ts">
import { format } from 'date-fns'
import { storeToRefs } from 'pinia'
import { computed, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import { LButton, LIcon, LModal, useDialog } from '@last/core-ui/paprika'

import { reportsApi } from '@/api/client'
import FullScreen from '@/components/FullScreen.vue'
import { useMoney } from '@/composables/useMoney'
import TicketPrinter from '@/integrations/printer/ticketPrinter'
import { useToast } from '@/plugins/toast/toast'
import { useAuthStore } from '@/store/auth'
import { useConfigStore } from '@/store/config'
import { useEmployeeStore } from '@/store/employee'
import { useTabsStore } from '@/store/tabs'
import { useTillStore } from '@/store/till'
import type { ZReport } from '@/types'

import ZReportSummary from './ZReportSummary.vue'

const router = useRouter()
const configStore = useConfigStore()
const authStore = useAuthStore()
const employeeStore = useEmployeeStore()
const tillStore = useTillStore()
const tabsStore = useTabsStore()

const { tills } = storeToRefs(configStore)
const { listEmployees } = storeToRefs(authStore)
const { startedShifts, dayShift } = storeToRefs(tillStore)
const { tabs } = storeToRefs(tabsStore)

const { getColorClass, shortName, formatClockInTime } = employeeStore
const { closeDay } = tillStore

const dialog = useDialog()
const { t } = useI18n()
const { currency } = useMoney()
const toast = useToast()

const showTillsModal = ref(false)
const showEmployeesModal = ref(false)
const showZReportModal = ref(false)
const showXReportModal = ref(false)

const todayReport = ref<ZReport | null>(null)
const zReport = ref<ZReport | null>(null)
const xReport = ref<ZReport | null>(null)

const tillsStatus = computed(() => {
  const cashTillCount = tills.value?.length || 0
  const employeeTillCount = listEmployees.value.filter(emp => emp.tillEnabled && emp.tillId).length
  const total = cashTillCount + employeeTillCount
  const open = startedShifts.value.length
  const closed = total - open
  return `${closed}/${total} ${t('end-shift.closed-tills')}`
})

const tabsStatus = computed(() => {
  const tabList = Object.values(tabs.value).filter(tab => tab.open && !!tab.activationTime)
  const open = tabList.filter(tab => tab.open === true).length
  const closed = tabList.length - open
  return `${closed}/${tabList.length} ${t('end-shift.closed-tabs')}`
})

const employeesStatus = computed(() => {
  const total = listEmployees.value.length
  const inactive = listEmployees.value.filter(emp => !emp.clockIn || emp.clockOut).length
  return `${inactive}/${total} ${t('end-shift.clocked-out-employees')}`
})

const allTabsClosed = computed(() => {
  const tabList = Object.values(tabs.value).filter(tab => tab.open && !!tab.activationTime)
  return tabList.filter(tab => tab.open === true).length === 0
})

const allTillsClosed = computed(() => {
  return startedShifts.value.length === 0
})

const allEmployeesClockedOut = computed(() => {
  return listEmployees.value.every(emp => !emp.clockIn || emp.clockOut)
})

const openCashTills = computed(() => {
  return tills.value?.filter(till => startedShifts.value.includes(till.id)) || []
})

const openEmployeeTills = computed(() => {
  const cashTillIds = new Set(tills.value?.map(till => till.id) || [])
  return listEmployees.value
    .filter(
      emp =>
        emp.tillEnabled &&
        emp.tillId &&
        startedShifts.value.includes(emp.tillId) &&
        !cashTillIds.has(emp.tillId)
    )
    .map(emp => ({ id: emp.tillId!, name: emp.name }))
})

const openTills = computed(() => {
  return [...openCashTills.value, ...openEmployeeTills.value]
})

const workingEmployees = computed(() => {
  return listEmployees.value.filter(emp => emp.clockIn && !emp.clockOut)
})

onMounted(async () => {
  todayReport.value = await reportsApi.getZReport({
    startDate: dayShift.value.start.toISOString(),
    endDate: new Date().toISOString()
  })
})

function closePage() {
  if (allTillsClosed.value && !dayShift.value.end) {
    dialog({
      title: t('end-shift.leave-title'),
      content: t('end-shift.leave-description'),
      secondaryLabel: t('ctas.cancel'),
      mainLabel: t('ctas.continue'),
      onConfirm: () => {
        router.push({ name: 'employees' })
      }
    })
  } else {
    router.push({ name: 'employees' })
  }
}

function tryCloseDay() {
  dialog({
    title: t('end-shift.close-day-generate'),
    content: t('end-shift.close-day-confirmation'),
    secondaryLabel: t('ctas.cancel'),
    mainLabel: t('end-shift.close-day'),
    onConfirm: () => confirmCloseDay()
  })
}

async function printZ(report?: ZReport | null) {
  zReport.value = report ? report : await reportsApi.getZReport()
  return TicketPrinter.printZReport(zReport.value)
}

async function closeShift() {
  xReport.value = await reportsApi.getXReport()
  printX(xReport.value)
  showXReportModal.value = true
}

function printX(report?: ZReport | null) {
  return TicketPrinter.printXReport(report)
}

function startClosingCashTill(tillId: string) {
  showTillsModal.value = false
  router.push({ name: 'endTillShift', params: { tillId } })
}

function handleShowTillsModal() {
  if (startedShifts.value.length === 1 && !allTabsClosed.value) {
    dialog({
      title: t('end-shift.open-tabs-title'),
      content: t('end-shift.open-tabs-message'),
      secondaryLabel: t('ctas.cancel'),
      mainLabel: t('end-shift.tabs.action'),
      onConfirm: () => navigateToTabs()
    })
  } else {
    showTillsModal.value = true
  }
}

function navigateToTabs() {
  router.push({ name: 'tabs' })
}

function onDayClosed() {
  toast({ type: 'success', title: t('end-shift.day-closed-success') })
  router.push({ name: 'employees' })
}

function onShiftClosed() {
  toast({ type: 'success', title: t('end-shift.shift-closed-success') })
  router.push({ name: 'employees' })
}

async function confirmCloseDay() {
  const shiftStart = format(dayShift.value.start, 'yyyy-MM-dd')
  const today = format(new Date(), 'yyyy-MM-dd')
  const response = await reportsApi.getLocationShiftsQuantity({
    startDate: shiftStart,
    endDate: today
  })
  const multipleTurns = response.quantity > 1
  await closeDay()
  if (multipleTurns) {
    await printX()
  }
  await printZ()
  showZReportModal.value = true
}
</script>
